/**
 * Lançamento de recebimento — um ou vários arquivos de uma vez.
 *
 * Fluxo definido com o cliente: **seleciona primeiro a operadora** — só
 * aparecem as que têm modelo aprovado — e então envia os arquivos.
 *
 * **Por que a competência é por arquivo, e não do envio inteiro.** A Unimed
 * manda de 6 a 8 PDFs para a mesma referência de pagamento, mas um envio
 * também pode carregar demonstrativos atrasados junto com os do mês. Uma
 * competência única no topo forçaria dois envios sempre que isso acontecesse.
 * O campo "aplicar a todos" resolve o caso comum em um clique, e cada linha
 * continua editável para o caso que foge dele.
 *
 * **Por que um só fluxo para um arquivo ou para oito.** Manter uma tela para
 * envio único e outra para lote seria duas UX e dois caminhos de código para
 * o mesmo ato. Com um arquivo, a fila resolve em segundos e a tela de
 * acompanhamento mostra exatamente as mesmas conferências.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import { enviarLote, listarOperadoras, orientacaoDeEnvio } from '../api/fluxfin';
import {
  Alerta,
  Botao,
  LoadingSpinner,
  PageHeader,
  Painel,
  Tabela,
  Th,
} from '../components/common';
import {
  competenciaAtual,
  erroCompetencia,
  mascararCompetencia,
} from '../utils/formato';

const MAX_ARQUIVOS = 30;

function tamanho(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function ArquivoNovo() {
  const navigate = useNavigate();

  const [operadoras, setOperadoras] = useState([]);
  const [idOperadora, setIdOperadora] = useState('');
  // Cada entrada: { arquivo: File, competencia: string, erro: string }
  const [selecionados, setSelecionados] = useState([]);
  const [competenciaGeral, setCompetenciaGeral] = useState(competenciaAtual());
  const [sincronizar, setSincronizar] = useState(true);
  // O que esta operadora manda — vem do servidor, que conhece os conjuntos.
  const [orientacao, setOrientacao] = useState(null);

  const [progresso, setProgresso] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    listarOperadoras()
      .then((r) => setOperadoras(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar as operadoras.')))
      .finally(() => setCarregando(false));
  }, []);

  const disponiveis = operadoras.filter((o) => o.tem_modelo_aprovado);
  const selecionada = operadoras.find((o) => o.id_operadora === idOperadora);

  // Buscada ao escolher a operadora, e mostrada logo abaixo do seletor: e ali
  // que a duvida "mando os dois ou so um?" aparece.
  useEffect(() => {
    if (!idOperadora) {
      setOrientacao(null);
      return;
    }
    orientacaoDeEnvio(idOperadora)
      .then((r) => setOrientacao(r.data))
      .catch(() => setOrientacao(null));
  }, [idOperadora]);

  const acrescentar = (lista) => {
    const novos = Array.from(lista).map((arquivo) => ({
      arquivo,
      competencia: competenciaGeral,
      erro: '',
    }));
    setSelecionados((atual) => {
      // O mesmo arquivo escolhido duas vezes seria recusado pelo servidor
      // como duplicado; barrar aqui poupa a viagem e a mensagem de erro.
      const jaTem = new Set(atual.map((s) => `${s.arquivo.name}|${s.arquivo.size}`));
      const filtrados = novos.filter(
        (n) => !jaTem.has(`${n.arquivo.name}|${n.arquivo.size}`)
      );
      return [...atual, ...filtrados].slice(0, MAX_ARQUIVOS);
    });
  };

  const alterar = (i, campo, valor) =>
    setSelecionados((atual) =>
      atual.map((s, idx) => (idx === i ? { ...s, [campo]: valor } : s))
    );

  const remover = (i) =>
    setSelecionados((atual) => atual.filter((_, idx) => idx !== i));

  const aplicarATodos = () =>
    setSelecionados((atual) =>
      atual.map((s) => ({ ...s, competencia: competenciaGeral, erro: '' }))
    );

  const totalBytes = useMemo(
    () => selecionados.reduce((s, i) => s + i.arquivo.size, 0),
    [selecionados]
  );

  const submeter = async (e) => {
    e.preventDefault();
    if (!idOperadora || selecionados.length === 0) return;

    // Competência fora do formato barra o envio: ela organiza filtros e
    // agrupamentos, e um valor torto só apareceria depois, na conciliação.
    const comErro = selecionados.map((s) => ({
      ...s,
      erro: erroCompetencia(s.competencia),
    }));
    if (comErro.some((s) => s.erro)) {
      setSelecionados(comErro);
      setErro('Corrija a competência dos arquivos marcados antes de enviar.');
      return;
    }

    setErro('');
    setEnviando(true);
    setProgresso(0);
    try {
      const { data } = await enviarLote({
        idOperadora,
        arquivos: selecionados.map((s) => s.arquivo),
        competencias: selecionados.map((s) => s.competencia),
        sincronizarBioslab: sincronizar,
        onProgress: setProgresso,
      });
      navigate(`/lotes/${data.id_lote}`);
    } catch (err) {
      setErro(mensagemErro(err, 'Não foi possível enviar os arquivos.'));
      setEnviando(false);
    }
  };

  if (carregando) return <LoadingSpinner />;

  return (
    <div style={{ maxWidth: 960 }}>
      <PageHeader
        titulo="Lançar recebimento"
        subtitulo="Selecione a operadora e envie um ou vários demonstrativos — inclusive de competências diferentes."
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {disponiveis.length === 0 && (
        <Alerta tipo="atencao">
          Nenhuma operadora tem modelo de leitura aprovado ainda. Um administrador precisa
          cadastrar e aprovar o modelo antes de receber arquivos.
        </Alerta>
      )}

      <Painel>
        <form onSubmit={submeter} className="p-4 d-flex flex-column gap-3">
          <div className="ff-campo">
            <label htmlFor="operadora">Operadora</label>
            <select
              id="operadora"
              className="ff-select"
              value={idOperadora}
              onChange={(e) => setIdOperadora(e.target.value)}
              required
              disabled={enviando}
            >
              <option value="">Selecione…</option>
              {disponiveis.map((o) => (
                <option key={o.id_operadora} value={o.id_operadora}>
                  {o.nome}
                </option>
              ))}
            </select>
            <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
              Só aparecem operadoras com modelo de leitura aprovado. Todos os arquivos do
              envio são desta operadora.
            </span>
          </div>

          {orientacao?.orientacao && (
            <div className="ff-aviso ff-aviso--info" style={{ margin: 0 }}>
              <i className="bi bi-info-circle" aria-hidden="true" />
              <div>
                <strong>{selecionada?.nome}</strong> — {orientacao.orientacao}
                {(orientacao.conjuntos || []).map((c) => (
                  <div key={c.id_conjunto} style={{ fontSize: 13, marginTop: 4 }}>
                    <em>{c.nome}</em>: {c.rotulo_relacao}
                    {c.modelos.length > 0 && (
                      <span style={{ color: 'var(--ff-muted)' }}>
                        {' — '}
                        {c.modelos
                          .filter((m) => m.liberado)
                          .map((m) => `${m.formato} (${m.papel.replace(/_/g, ' ')})`)
                          .join(' · ')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="ff-campo">
            <label htmlFor="arquivos">Arquivos</label>
            <input
              id="arquivos"
              type="file"
              multiple
              className="form-control"
              style={{ borderRadius: 8, borderColor: 'var(--ff-border-field)' }}
              onChange={(e) => {
                acrescentar(e.target.files || []);
                // Zera para permitir escolher o mesmo arquivo de novo depois
                // de removê-lo da lista.
                e.target.value = '';
              }}
              disabled={enviando || !idOperadora}
            />
            <span style={{ fontSize: 13, color: 'var(--ff-muted-2)' }}>
              Até {MAX_ARQUIVOS} por envio. Pode selecionar vários de uma vez, ou ir
              acrescentando.
              {selecionada && ` Cada arquivo precisa corresponder a um modelo de ${selecionada.nome}.`}
            </span>
          </div>

          {selecionados.length > 0 && (
            <>
              <div className="d-flex flex-wrap align-items-end gap-2">
                <div className="ff-campo" style={{ margin: 0 }}>
                  <label htmlFor="comp-geral">Competência para todos</label>
                  <input
                    id="comp-geral"
                    className="ff-input ff-input--valor"
                    style={{ width: 150 }}
                    placeholder="MM/AAAA"
                    value={competenciaGeral}
                    onChange={(e) =>
                      setCompetenciaGeral(mascararCompetencia(e.target.value))
                    }
                    inputMode="numeric"
                    maxLength={7}
                    disabled={enviando}
                  />
                </div>
                <Botao variante="secundario" onClick={aplicarATodos} disabled={enviando}>
                  Aplicar a todos
                </Botao>
                <span style={{ fontSize: 13, color: 'var(--ff-muted-2)', paddingBottom: 8 }}>
                  Cada linha pode ter a sua — um envio pode cobrir mais de uma competência.
                </span>
              </div>

              <Tabela estreita>
                <thead>
                  <tr>
                    <Th>Arquivo</Th>
                    <Th alinhar="right">Tamanho</Th>
                    <Th>Competência</Th>
                    <Th alinhar="right">{''}</Th>
                  </tr>
                </thead>
                <tbody>
                  {selecionados.map((s, i) => (
                    <tr key={`${s.arquivo.name}-${s.arquivo.size}-${i}`}>
                      <td className="col-codigo">{s.arquivo.name}</td>
                      <td className="col-qtd">{tamanho(s.arquivo.size)}</td>
                      <td>
                        <input
                          className={`ff-input ff-input--valor${s.erro ? ' ff-input--erro' : ''}`}
                          style={{ width: 130, height: 34 }}
                          placeholder="MM/AAAA"
                          value={s.competencia}
                          onChange={(e) => {
                            alterar(i, 'competencia', mascararCompetencia(e.target.value));
                            if (s.erro) alterar(i, 'erro', '');
                          }}
                          onBlur={(e) =>
                            alterar(i, 'erro', erroCompetencia(e.target.value))
                          }
                          inputMode="numeric"
                          maxLength={7}
                          aria-label={`Competência de ${s.arquivo.name}`}
                          disabled={enviando}
                        />
                        {s.erro && (
                          <div className="ff-erro-msg" style={{ fontSize: 12 }}>
                            {s.erro}
                          </div>
                        )}
                      </td>
                      <td className="col-data">
                        <Botao
                          variante="secundario"
                          pequeno
                          onClick={() => remover(i)}
                          disabled={enviando}
                        >
                          Remover
                        </Botao>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th>
                      {selecionados.length} arquivo{selecionados.length === 1 ? '' : 's'}
                    </th>
                    <th className="col-qtd">{tamanho(totalBytes)}</th>
                    <th colSpan={2} />
                  </tr>
                </tfoot>
              </Tabela>
            </>
          )}

          <div className="ff-campo" style={{ margin: 0 }}>
            <label className="d-flex align-items-start gap-2" style={{ fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={sincronizar}
                onChange={(e) => setSincronizar(e.target.checked)}
                disabled={enviando}
                style={{ marginTop: 3 }}
              />
              <span>
                Consultar o Bioslab ao final de cada arquivo
                <span
                  style={{ display: 'block', fontSize: 13, color: 'var(--ff-muted-2)' }}
                >
                  Resolve as guias para seus protocolos, um arquivo de cada vez, em lotes —
                  é a produção do cliente, então a fila não paraleliza. Isso{' '}
                  <strong>não confirma nada</strong>: a revisão humana continua necessária
                  antes de qualquer valor virar baixa.
                </span>
              </span>
            </label>
          </div>

          {enviando && (
            <div>
              <div className="progress" style={{ height: 6, borderRadius: 3 }}>
                <div
                  className="progress-bar"
                  style={{ width: `${progresso}%`, background: 'var(--ff-blue)' }}
                />
              </div>
              <small style={{ color: 'var(--ff-muted-2)' }}>
                {progresso < 100
                  ? `Enviando… ${progresso}%`
                  : 'Arquivos recebidos — abrindo o acompanhamento.'}
              </small>
            </div>
          )}

          <div>
            <button
              type="submit"
              className="ff-btn ff-btn--primario"
              disabled={enviando || !idOperadora || selecionados.length === 0}
            >
              {enviando
                ? 'Enviando…'
                : `Enviar ${selecionados.length || ''} arquivo${selecionados.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </form>
      </Painel>
    </div>
  );
}
